import { ApolloError, useMutation } from "@apollo/client";
import { useEffect, useState } from "react";
import { Layout, Responsive, WidthProvider } from "react-grid-layout";
import { utils, writeFile } from "xlsx";

import {
  CollectionProductPriorityInput,
  UPDATE_COLLECTION_PRODUCTS_ORDER,
  UpdateCollectionProductsOrderInput,
  UpdateCollectionProductsOrderObject,
} from "~/api/graphql/Collection";
import { CustomButton } from "~/components/form/CustomButton";
import { SimpleLoader } from "~/components/UI/SimpleLoader";
import { colors } from "~/constants/styles";
import { CollectionModel } from "~/types/data/Collection.type";
import { Product } from "~/types/data/Product.types";

import styles from "./index.module.scss";
import { ProductCard } from "./ProductCard";

interface Props {
  collection?: CollectionModel;
  products: Product[];
  disabled: boolean;
  updateProductsSuccessHandler: (product: Product) => void;
  updateProductsOrderSuccessHandler: (products: Product[]) => void;
}
interface ProductGridCardType {
  product: Product;
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  static?: boolean;
}

const ResponsiveGridLayout = WidthProvider(Responsive);

export const ProductsList = ({
  collection,
  products,
  disabled,
  updateProductsSuccessHandler,
}: Props) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);
  const [orderUpdated, setOrderUpdated] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const productsCards: ProductGridCardType[] = products.map(
    (product, index) => {
      return {
        i: index.toString(),
        h: 1,
        w: 1,
        x: 0,
        y: index,
        product,
      };
    }
  );

  const [productsCardsState, setProductsCardsState] =
    useState<ProductGridCardType[]>(productsCards);

  const updateSubscriptionProductsOrderErrorHandler = (error: ApolloError) => {
    setErrorMessage(error.message);
  };

  const updateSubscriptionProductsOrderSuccessHandler = () => {
    setOrderUpdated(false);
  };

  const [updateCollectionProductsOrder] = useMutation<
    undefined,
    UpdateCollectionProductsOrderInput
  >(UPDATE_COLLECTION_PRODUCTS_ORDER);

  const changeLayoutHandler = (layout: Layout[]) => {
    if (!orderUpdated) setOrderUpdated(true);

    const newProductsCardsState: (ProductGridCardType | undefined)[] =
      layout.map((layoutItem) => {
        const productCard = productsCardsState.find(
          (productCard) => productCard.i === layoutItem.i
        );
        if (!productCard) return;
        return {
          ...productCard,
          y: layoutItem.y,
        };
      });

    const filteredList = newProductsCardsState.filter(
      (state) => state !== undefined
    ) as ProductGridCardType[];

    setProductsCardsState(filteredList);
  };

  const changeOrderSubmitHandler = async () => {
    setIsLoading(true);
    setErrorMessage("");

    const priorities: CollectionProductPriorityInput[] = productsCardsState.map(
      (productCard) => {
        return {
          collectionId: collection?.id || 0,
          productParentId: productCard.product.parentId ?? "",
          priority: productCard.y + 1,
        };
      }
    );

    const input: UpdateCollectionProductsOrderObject = {
      collectionId: collection?.id || 0,
      priorities,
    };

    await updateCollectionProductsOrder({
      variables: {
        UpdateCollectionProductsOrderInput: input,
      },
      onCompleted: updateSubscriptionProductsOrderSuccessHandler,
      onError: updateSubscriptionProductsOrderErrorHandler,
    });
    setIsLoading(false);
  };

  const downloadProducts = () => {
    const mappedProducts = productsCardsState.map(({ y, product }) => {
      const {
        sku,
        secondHandPrice,
        discountedSecondHandPrice,
        priorityInCollection,
      } = product;
      return {
        sku,
        secondHandPrice,
        discountedSecondHandPrice,
        priority: priorityInCollection,
      };
    });

    // create file using xlsx
    const wb = utils.book_new();
    const ws = utils.json_to_sheet(mappedProducts);

    // make cells wrap text
    utils.book_append_sheet(wb, ws, `Products_${collection?.name}`);
    setDownloadLoading(false);
    writeFile(wb, `Products_${collection?.name}.xlsx`);
  };

  const exportCatalogueHandler = () => {
    setDownloadLoading(true);
    setTimeout(() => {
      downloadProducts();
    }, 500);
  };

  useEffect(() => {
    setProductsCardsState(productsCards);
  }, [collection]);

  console.log(productsCards);
  return (
    <div className={styles.container}>
      <div className={styles.headerContainer}>
        <div>
          <h3>Products for {collection?.name}</h3>
          <CustomButton
            backgroundColor={colors.$primary}
            color="white"
            width="fit-content"
            borderRadius="8px"
            padding="1rem 1.5rem"
            type="submit"
            disabled={downloadLoading}
            height="1rem"
            onClick={() => {
              exportCatalogueHandler();
            }}
          >
            {downloadLoading ? <SimpleLoader size="size2" /> : "Download Data"}
          </CustomButton>
        </div>
        <CustomButton
          backgroundColor={colors.$primary}
          color="white"
          width="fit-content"
          borderRadius="8px"
          padding="1rem 1.5rem"
          disabled={isLoading || !orderUpdated}
          type="submit"
          height="1rem"
          onClick={() => {
            changeOrderSubmitHandler();
          }}
        >
          {isLoading ? <SimpleLoader size="size2" /> : "Enregistrer"}
        </CustomButton>
      </div>
      {!!errorMessage && (
        <div className={styles.errorContainer}>{errorMessage}</div>
      )}
      <div>
        <ResponsiveGridLayout
          breakpoints={{ lg: 1200 }}
          cols={{ lg: 1 }}
          isBounded={true}
          isResizable={false}
          margin={[10, 80]}
          containerPadding={[0, 0]}
          compactType="vertical"
          onDragStop={(l) => {
            setTimeout(() => {
              changeLayoutHandler(l);
            }, 100);
          }}
          layouts={{ lg: productsCards }}
          draggableCancel=".allow-select"
        >
          {productsCards.map((productCard) => (
            <div
              key={productCard.i}
              data-grid={{
                x: productCard.x,
                y: productCard.y,
                w: productCard.w,
                h: productCard.h,
                static: productCard.static,
              }}
            >
              <ProductCard
                product={productCard.product}
                key={productCard.product.id}
                disabled={disabled}
                updateProductsSuccessHandler={updateProductsSuccessHandler}
                collectionId={collection?.id || 0}
              />
            </div>
          ))}
        </ResponsiveGridLayout>
      </div>
    </div>
  );
};
